import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { ShowcaseCategoryViewStore } from "../../store";
import { ShowcaseCategoryView } from "../view/ShowcaseCategoryView";
import { ShowcaseCategoryUpdateStore } from "../../store";
import { ShowcaseCategoryUpdate } from "../update/ShowcaseCategoryUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type ShowcaseCategoryModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const ShowcaseCategoryModalContent: React.FC<ShowcaseCategoryModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new ShowcaseCategoryUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new ShowcaseCategoryViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <ShowcaseCategoryView
            showcaseCategoryViewStore={
              storeInfo.store as ShowcaseCategoryViewStore
            }
          />
        )}
        {storeInfo.type === "update" && (
          <ShowcaseCategoryUpdate
            showcaseCategoryUpdateStore={
              storeInfo.store as ShowcaseCategoryUpdateStore
            }
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
